/**
 * Utility functions for parsing and formatting backend API errors into
 * specific, user-actionable error messages.
 */

/**
 * Humanizes raw API field keys into readable labels.
 * E.g. "lesson_title" -> "Lesson Title", "body.outline_text" -> "Outline Text", "items.0.duration" -> "Duration"
 */
export function humanizeFieldName(rawField: string): string {
  if (!rawField) return "";
  const cleaned = rawField.replace(/^(body|query|path)(\.|\s*->\s*)/g, "").trim();
  if (!cleaned) return "";

  const parts = cleaned.split(/[.->]+/).filter(Boolean);
  // Find the last non-numeric path segment
  const nonNumeric = [...parts].reverse().find((p) => !/^\d+$/.test(p));
  if (!nonNumeric) return "";

  return nonNumeric
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Extracts specific, human-readable error messages from any API error shape,
 * identifying which field has an issue and what is required rather than displaying
 * generic placeholders like "Invalid request body".
 */
export function formatApiErrorMessage(err: unknown, fallback: string = "An unexpected error occurred"): string {
  if (!err) return fallback;

  // 1. If string
  if (typeof err === "string") {
    const trimmed = err.trim();
    if (!trimmed) return fallback;
    // Check if it's a JSON string
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        const parsed = JSON.parse(trimmed);
        return formatApiErrorMessage(parsed, fallback);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  // 2. If standard Error instance
  if (err instanceof Error) {
    if (err.message && err.message.trim()) {
      return formatApiErrorMessage(err.message, fallback);
    }
    return fallback;
  }

  // 3. If object
  if (typeof err === "object") {
    const errObj = err as Record<string, any>;

    // Case A: { errors: [ { field?: string, message: string } ] }
    if (Array.isArray(errObj.errors) && errObj.errors.length > 0) {
      const parts = errObj.errors
        .map((e: any) => {
          if (!e) return null;
          if (typeof e === "string") return e;
          let msg = e.message || e.msg || "Invalid value";
          if (typeof msg === "string" && msg.startsWith("Value error, ")) {
            msg = msg.slice("Value error, ".length);
          }
          const rawField = e.field || "";
          const label = humanizeFieldName(rawField);
          if (typeof msg === "string" && msg.toLowerCase() === "field required" && label) {
            return `${label} is required`;
          }
          return label ? `${label}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (parts.length > 0) return parts.join("; ");
    }

    // Case B: FastAPI standard { detail: [ { loc: [...], msg: string, type: string } ] }
    if (Array.isArray(errObj.detail) && errObj.detail.length > 0) {
      const parts = errObj.detail
        .map((d: any) => {
          if (!d) return null;
          if (typeof d === "string") return d;
          let msg = d.msg || d.message || "Invalid value";
          if (typeof msg === "string" && msg.startsWith("Value error, ")) {
            msg = msg.slice("Value error, ".length);
          }
          const locParts = Array.isArray(d.loc)
            ? d.loc.filter((p: any) => p !== "body" && p !== "query" && p !== "path").map(String)
            : [];
          const rawField = locParts.join(".");
          const label = humanizeFieldName(rawField);
          if (typeof msg === "string" && msg.toLowerCase() === "field required" && label) {
            return `${label} is required`;
          }
          return label ? `${label}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (parts.length > 0) return parts.join("; ");
    }

    // Case C: { detail: string }
    if (typeof errObj.detail === "string" && errObj.detail.trim()) {
      return errObj.detail.trim();
    }

    // Case D: { error: string }
    if (typeof errObj.error === "string" && errObj.error.trim()) {
      return errObj.error.trim();
    }

    // Case E: { message: string }
    if (typeof errObj.message === "string" && errObj.message.trim()) {
      return errObj.message.trim();
    }

    // Case F: Nested response or data wrapper (e.g. from Axios/Hey-API/Fetch)
    if (errObj.response?.data) {
      return formatApiErrorMessage(errObj.response.data, fallback);
    }
    if (errObj.data && errObj.data !== errObj) {
      return formatApiErrorMessage(errObj.data, fallback);
    }
  }

  return fallback;
}

export default formatApiErrorMessage;
